/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FIELD_QUANTITY } from '@components/dynamic-input-fields/src/utils';
import { FillInfo } from '@components/forms/invite-guest/fill-info';
import { SelectType } from '@components/forms/invite-guest/select-type';
import { IconDownload } from '@components/icons';
import { SummarySuccessModalContent } from '@components/modals';
import { PROCESS_IMPORT_ROUTE } from '@constants/routes';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { EventContext } from '@contexts/event/event-context';
import { ModalContext } from '@contexts/modal/modal-context';
import { useGetOfferAddon } from '@hooks/addons/use-get-offer-addons';
import { useGetAdmission } from '@hooks/admissions/use-get-admission';
import { useImportMultiInvite } from '@hooks/import/use-import-multi-invite';
import { useAddGuest } from '@hooks/use-add-guest/use-add-guest';
import { IMPORT_TYPE_INVITATION } from '@pages/events/utils/import-process-utils';
import { createBookingLink } from '@utils/create-booking-link';

import { createFieldsFromBookingFormFields, createMultiInviteTextRows } from '../utils/invite-guest-utils';

import {
  InviteGuestForm,
  MULTIPLE_INVITE,
  OPTION_FILL_INFO,
  ProcessInviteGuestStep,
  SINGLE_INVITE,
  STEP_INFO,
  STEP_SELECT_TYPE,
  STEP_SUCCESS,
} from './process-invite-guest.type';
export const ProcessInviteGuest = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState<ProcessInviteGuestStep>(STEP_SELECT_TYPE);
  const {
    eventState: { id: paramEventId, isLiveEvent },
  } = useContext(EventContext);

  const {
    clubState: { slug: clubSlug },
    companyState: {
      legacyId: companyLegacyId,
      defaultChannel: { id: defaultChannelId },
    },
  } = useContext(CompanyClubContext);
  const { toggleModal, handleCloseModal, setPreventExit } = useContext(ModalContext);

  const methods = useForm<InviteGuestForm>({
    defaultValues: {
      eventId: isLiveEvent ? paramEventId ?? '' : '',
      invitationId: '',
      type: SINGLE_INVITE,
      quantity: 1,
      infoType: OPTION_FILL_INFO,
      channelId: defaultChannelId,
    },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    watch,
    formState: { isDirty },
  } = methods;

  const invitationId = watch('invitationId');
  const invitationType = watch('type');
  const eventId = watch('eventId');
  const email = watch('email');
  const channelId = watch('channelId');

  const { data: offer } = useGetAdmission({
    offerId: invitationId,
    channelId,
    options: {
      enabled: !!invitationId,
    },
  });
  const { data: addons } = useGetOfferAddon({
    offerId: invitationId,
    enabled: offer && offer?.addOns?.length > 0,
  });
  const { data: addGuestData, mutate: addGuest, isSuccess: addGuestSuccess } = useAddGuest({ hideToast: true });
  const {
    data: importMultiInviteData,
    mutate: importMultiInvite,
    isSuccess: importSuccess,
  } = useImportMultiInvite({ hideToast: true });

  const closeModal = useCallback(() => toggleModal({ isOpen: false, type: '' }), [toggleModal]);
  const newSendInvitationProcess = () => setStep(STEP_SELECT_TYPE);

  const createProcessPath = useCallback(
    (importId: string) => {
      if (!importId) return;
      return PROCESS_IMPORT_ROUTE.replace(':companyId', companyLegacyId)
        .replace(':clubSlug', clubSlug)
        .replace(':eventId', eventId)
        .replace(':importId', importId)
        .replace(':importType', IMPORT_TYPE_INVITATION);
    },
    [companyLegacyId, clubSlug, eventId]
  );

  const handleSingleInviteSubmit = (e: InviteGuestForm) => {
    addGuest({
      formValue: {
        offerId: e.invitationId,
        bookingFormFields: createFieldsFromBookingFormFields(e),
        ticketHolderFormFields: [],
        numTickets: e.quantity,
        channelId: e.channelId,
      },
    });
  };

  const handleMultiInviteSubmit = (e: InviteGuestForm) => {
    if (e.multiInviteText) {
      const rows = createMultiInviteTextRows(e.multiInviteText);
      importMultiInvite({
        importData: {
          offerId: e.invitationId,
          channelId: e.channelId,
          eventId: e.eventId,
          rows,
        },
      });
    }
  };

  const onSubmit = (e: InviteGuestForm) => {
    if (invitationType === SINGLE_INVITE) {
      handleSingleInviteSubmit(e);
    } else if (invitationType === MULTIPLE_INVITE) {
      handleMultiInviteSubmit(e);
    }
  };

  useEffect(() => {
    if (addGuestSuccess) {
      setStep(STEP_SUCCESS);
      setPreventExit(false);
    }
  }, [addGuestSuccess, setPreventExit]);

  useEffect(() => {
    if (importSuccess && importMultiInviteData) {
      const path = createProcessPath(importMultiInviteData?.importId);
      if (path) navigate(path);
      setPreventExit(false);
      closeModal();
    }
  }, [importSuccess, setPreventExit, importMultiInviteData, createProcessPath, eventId, navigate, closeModal]);

  useEffect(() => {
    setPreventExit(isDirty);
  }, [isDirty, setPreventExit]);

  return (
    <form className='flex justify-end flex-col relative'>
      <FormProvider {...methods}>
        {step === STEP_SELECT_TYPE && <SelectType onContinue={() => setStep(STEP_INFO)} offer={offer} />}
        {step === STEP_INFO && <FillInfo onContinue={handleSubmit(onSubmit)} handleClose={handleCloseModal} />}
        {step === STEP_SUCCESS && offer && addGuestData && (
          <SummarySuccessModalContent
            title={t('invitations:processInviteGuestSuccessTitle')}
            quantity={watch(FIELD_QUANTITY)}
            offer={offer}
            addons={addons ?? []}
            mainActionText={t('invitations:buttonAddNewGuest')}
            onClickSecondaryAction={() => createBookingLink(addGuestData.token)}
            secondaryActionIcon={IconDownload}
            headerDescription={
              email ? (
                <span className='text-body-s text-secondary'>
                  <Trans
                    i18nKey='offers:processEmailSuccessDescription'
                    components={{ b: <b /> }}
                    values={{ email }}></Trans>
                </span>
              ) : undefined
            }
            secondaryActionText={t('offers:downloadPdf')}
            onClickMainAction={newSendInvitationProcess}
            onClickClose={closeModal}
            isInvitation={true}
          />
        )}
      </FormProvider>
    </form>
  );
};
